import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface Props {
  id: string;
  colIdx: number;
  headerText: string;
  value: string;
  sovLines: any[];
  usedSovIds: Set<string>;
  onChange: (colIdx: number, value: string) => void;
}

export const SortableColumnRow = ({ id, colIdx, headerText, value, sovLines, usedSovIds, onChange }: Props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded-md px-2 py-1.5 group",
        isDragging ? "bg-accent z-10 shadow-md" : "hover:bg-muted/50"
      )}
    >
      <button
        type="button"
        className="cursor-grab opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <span className="text-sm font-mono w-[200px] truncate shrink-0" title={headerText}>
        {headerText}
      </span>

      <span className="text-muted-foreground shrink-0">→</span>

      <Select value={value} onValueChange={(v) => onChange(colIdx, v)}>
        <SelectTrigger className="w-[260px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="skip">Skip this column</SelectItem>
          <SelectItem value="unit_number">Unit Number (required)</SelectItem>
          <SelectItem value="floor">Floor Number</SelectItem>
          {sovLines.map((sl: any) => {
            const isUsedElsewhere = usedSovIds.has(sl.id) && value !== sl.id;
            return (
              <SelectItem key={sl.id} value={sl.id} disabled={isUsedElsewhere}>
                {sl.description} ({sl.unit}){isUsedElsewhere ? " ✓" : ""}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};
